import { useQuery } from "@tanstack/react-query";
import { useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { axiosInstance } from "@/pages/auth/auth.context";
import { Button } from "@/components/ui/button";
import { EventsTable } from "@/components/admin/events/EventsTable";
import { EventsTableSkeleton } from "@/components/admin/events/EventsTableSkeleton";
import { EventFilterComponent } from "@/components/admin/events/EventFilterComponent";
import { toast } from "@/components/ui/use-toast";
import { AdminEventSummaryDTO, Event } from "@/pages/admin/events/types";
import { TimeFilterEnum, MANAGE_EVENTS_CONFIG } from "@/types/eventFilter";
import { EventStatusEnum } from "@/types/eventStatus";
import { useDebounce } from "@/hooks/useDebounce";
import { DataTablePagination } from "@/components/ui/data-table-pagination";

const ManageEvents = () => {
  const [sortColumn, setSortColumn] = useState<keyof Event | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [selectedStatuses, setSelectedStatuses] = useState<EventStatusEnum[]>(
    MANAGE_EVENTS_CONFIG.defaultSelectedStatuses || [],
  );
  const [companyName, setCompanyName] = useState<string>("");
  const [contactName, setContactName] = useState<string>("");
  const [hasVacancies, setHasVacancies] = useState<boolean | undefined>(
    undefined,
  );
  const [timeFilter, setTimeFilter] = useState<TimeFilterEnum | undefined>(
    TimeFilterEnum.UPCOMING,
  );
  const [startDateFrom, setStartDateFrom] = useState<string>("");
  const [startDateTo, setStartDateTo] = useState<string>("");
  const [endDateFrom, setEndDateFrom] = useState<string>("");
  const [endDateTo, setEndDateTo] = useState<string>("");

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize] = useState<number>(20);
  const [totalPages, setTotalPages] = useState<number>(0);

  const debouncedCompanyName = useDebounce(companyName, 500);
  const debouncedContactName = useDebounce(contactName, 500);

  // Reset page when any filter changes
  useEffect(() => {
    setCurrentPage(0);
  }, [
    selectedStatuses,
    debouncedCompanyName,
    debouncedContactName,
    hasVacancies,
    timeFilter,
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
  ]);

  const params = new URLSearchParams();
  if (selectedStatuses.length > 0) {
    selectedStatuses.forEach((status) => params.append("statuses", status));
  }
  if (startDateFrom) params.append("startDateFrom", startDateFrom);
  if (startDateTo) params.append("startDateTo", startDateTo);
  if (endDateFrom) params.append("endDateFrom", endDateFrom);
  if (endDateTo) params.append("endDateTo", endDateTo);
  if (debouncedContactName.trim())
    params.append("contactName", debouncedContactName.trim());
  if (debouncedCompanyName.trim())
    params.append("companyName", debouncedCompanyName.trim());
  if (hasVacancies !== undefined)
    params.append("hasVacancies", hasVacancies.toString());
  if (timeFilter) params.append("timeFilter", timeFilter);
  params.append("size", pageSize.toString());
  params.append("page", currentPage.toString());

  const { data: events, isLoading } = useQuery({
    queryKey: [
      "events",
      "admin",
      selectedStatuses,
      debouncedCompanyName,
      debouncedContactName,
      hasVacancies,
      timeFilter,
      startDateFrom,
      startDateTo,
      endDateFrom,
      endDateTo,
      currentPage,
      pageSize,
    ],
    queryFn: async () => {
      const queryString = params.toString();
      const response = await axiosInstance.get(
        `event/admin${queryString ? `?${queryString}` : ""}`,
      );

      // Update pagination state
      if (response.data.page?.totalPages !== undefined) {
        setTotalPages(response.data.page.totalPages);
      }

      return (
        response.data.content?.map((event: AdminEventSummaryDTO): Event => ({
          id: event.id,
          title: event.companyName || "No Title",
          date: event.startTime
            ? new Date(event.startTime).toLocaleDateString()
            : "",
          time: event.startTime
            ? new Date(event.startTime).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "",
          location: event.city || "No Location",
          staffNeeded: event.totalPositions || 0,
          staffAssigned: event.filledPositions || 0,
          attendees: event.attendees || 0,
          totalTables: event.totalTables || 0,
          filledTables: event.filledTables || 0,
          paymentStatus: event.paymentStatus,
        })) || []
      );
    },
    meta: {
      onError: (error) => {
        toast({
          title: "Error",
          description: "Unable to fetch events.",
          variant: "destructive",
        });
      },
    },
  });

  const handleSortColumn = useCallback(
    (column: keyof Event) => {
      if (sortColumn === column) {
        setSortDirection((prevDirection) =>
          prevDirection === "asc" ? "desc" : "asc",
        );
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [sortColumn],
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const sortedEvents = useMemo(() => {
    if (!events) return [];
    if (!sortColumn) return events;

    return [...events].sort((a: Event, b: Event) => {
      let valA = a[sortColumn];
      let valB = b[sortColumn];

      if (sortColumn === "date") {
        valA = new Date(valA).getTime();
        valB = new Date(valB).getTime();
      }

      if (sortDirection === "asc") {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });
  }, [events, sortColumn, sortDirection]);

  return (
    <div className="min-h-screen p-2 sm:p-6">
      <div className="max-w-7xl mx-auto w-full px-0 sm:px-0">
        <header className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">
                Manage Events
              </h1>
              <p className="text-muted-foreground">
                View and manage all casino events
              </p>
            </div>
            <Link to="/admin/events/create">
              <Button>Create New Event</Button>
            </Link>
          </div>
        </header>

        <EventFilterComponent
          selectedStatuses={selectedStatuses}
          companyName={companyName}
          contactName={contactName}
          hasVacancies={hasVacancies}
          timeFilter={timeFilter}
          startDateFrom={startDateFrom}
          startDateTo={startDateTo}
          endDateFrom={endDateFrom}
          endDateTo={endDateTo}
          onStatusesChange={setSelectedStatuses}
          onCompanyNameChange={setCompanyName}
          onContactNameChange={setContactName}
          onHasVacanciesChange={setHasVacancies}
          onTimeFilterChange={setTimeFilter}
          onStartDateFromChange={setStartDateFrom}
          onStartDateToChange={setStartDateTo}
          onEndDateFromChange={setEndDateFrom}
          onEndDateToChange={setEndDateTo}
          allowedStatuses={MANAGE_EVENTS_CONFIG.allowedStatuses}
          showStatusFilter={MANAGE_EVENTS_CONFIG.showStatusFilter}
          showTimeFilter={MANAGE_EVENTS_CONFIG.showTimeFilter}
          showVacancyFilter={MANAGE_EVENTS_CONFIG.showVacancyFilter}
          showDateFilters={MANAGE_EVENTS_CONFIG.showDateFilters}
          showClientFilters={MANAGE_EVENTS_CONFIG.showClientFilters}
          showCompanyNameFilter={MANAGE_EVENTS_CONFIG.showCompanyNameFilter}
        />

        {isLoading ? (
          <EventsTableSkeleton />
        ) : (
          <div className="rounded-md border w-full">
            <EventsTable
              events={sortedEvents}
              onSortColumn={handleSortColumn}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
            />
          </div>
        )}

        <DataTablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default ManageEvents;
